import { Spinner } from "@common";
import {
  ConsultantPresentationCountry,
  ConsultantPresentationType,
  useGetConsultantShareableFilesQuery,
  GeneratePresentationAttachmentError,
  ConsultantReferenceRelation,
  ConsultantReferenceDocumentFragment
} from "@bonliva-crm/graphql";
import {
  DocumentList,
  ConsultantReferenceDocumentList,
  TextField,
  Color
} from "@bonliva-crm/ui";
import { useLocale, useSnackbars } from "@hooks";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Typography
} from "@material-ui/core";
import { Controller, UseFormReturn } from "react-hook-form";
import { UserSearchSingleModal } from "../../user-search/user-search-single-modal";
import { FirstFormValues } from "../utils/form-values";
import { Alert, AlertTitle } from "@material-ui/lab";
import { useMemo } from "react";
import { groupBy, sortBy } from "lodash";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

type Props = {
  consultantId: string;
  form: UseFormReturn<FirstFormValues, any, FirstFormValues>;
  attachmentErrors: GeneratePresentationAttachmentError[];
};

export const FirstStep = ({ form, consultantId, attachmentErrors }: Props) => {
  const classes = useStyles();
  const snack = useSnackbars();
  const { locale } = useLocale();
  const { watch, control } = form;

  const getFilesQuery = useGetConsultantShareableFilesQuery({
    variables: { consultantId: consultantId },
    fetchPolicy: "cache-and-network",
    onError: (err) => {
      console.error(err);
      snack.showError(locale.ErrorOccurred);
    }
  });

  const selectedCountry = watch("presentationCountry");
  const generateCV = watch("generateCV") === "true";

  const selectedPresentationType =
    watch("presentationType") === ConsultantPresentationType.CustomText
      ? locale.WithIntroText
      : locale.WithoutIntroText;

  const documents = useMemo(() => {
    return sortBy(
      getFilesQuery.data?.consultantById.DocumentsShareable,
      (file) => file.DocumentType
    );
  }, [getFilesQuery.data?.consultantById.DocumentsShareable]);

  const referenceDocuments = useMemo(() => {
    return groupBy(getFilesQuery.data?.consultantReferenceDocuments, (x) => {
      return x.workRelation;
    }) as Record<ConsultantReferenceRelation, ConsultantReferenceDocumentFragment[]>;
  }, [getFilesQuery.data?.consultantReferenceDocuments]);

  if (getFilesQuery.loading) {
    return <Spinner fullHeight />;
  }

  const selectedItems = [
    ...watch("selectedFiles"),
    ...watch("selectedReferenceDocuments")
  ];

  const selectedItemsText = locale.numberOfChosenObjects(
    selectedItems.length,
    locale.File.toLowerCase(),
    locale.Files.toLowerCase()
  );

  const allRefEvolLength = getFilesQuery.data?.consultantReferenceDocuments.length ?? 0;
  const allRefEvolDocsLength = allRefEvolLength + documents?.length;

  return (
    <div>
      <FormControl component="fieldset">
        <Grid container direction={"column"} spacing={2}>
          <Grid item>
            <Accordion>
              <AccordionSummary style={{ padding: 0 }}>
                <Typography variant="body1">
                  {locale.BeRepresentedIn} ({locale.countries[selectedCountry]})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Controller
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value={ConsultantPresentationCountry.Sweden}
                        control={<Radio />}
                        label={locale.countries.Sweden}
                      />
                      <FormControlLabel
                        value={ConsultantPresentationCountry.Norway}
                        control={<Radio />}
                        label={locale.countries.Norway}
                      />
                      <FormControlLabel
                        value={ConsultantPresentationCountry.Denmark}
                        control={<Radio />}
                        label={locale.countries.Denmark}
                      />
                    </RadioGroup>
                  )}
                  name="presentationCountry"
                  control={control}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item>
            <Accordion>
              <AccordionSummary style={{ padding: 0 }}>
                <Grid container alignItems={"center"} spacing={2}>
                  <Grid item>
                    <Typography variant="body1">
                      {locale.ShouldCRMGeneratedCVBeIncluded} (
                      {generateCV ? locale.Yes : locale.No})
                    </Typography>
                  </Grid>
                  {generateCV && (
                    <Grid item>
                      <OpenInNewIcon
                        fontSize={"small"}
                        htmlColor={Color.DetailsInput}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `/consultant-cv/${consultantId}/pdf?country=${selectedCountry}`
                          );
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Controller
                  render={({ field }) => (
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value={"true"}
                        control={<Radio />}
                        label={locale.Yes}
                      />
                      <FormControlLabel
                        value={"false"}
                        control={<Radio />}
                        label={locale.No}
                      />
                    </RadioGroup>
                  )}
                  name="generateCV"
                  control={control}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item>
            <Accordion>
              <AccordionSummary style={{ padding: 0 }}>
                <Typography variant="body1">
                  {locale.SelectPresentationType} ({selectedPresentationType})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container direction={"column"} spacing={2}>
                  <Grid item>
                    <Controller
                      render={({ field }) => (
                        <RadioGroup {...field}>
                          <FormControlLabel
                            value={ConsultantPresentationType.CustomText}
                            control={<Radio />}
                            label={locale.WithIntroText}
                          />
                          <FormControlLabel
                            value={ConsultantPresentationType.NoText}
                            control={<Radio />}
                            label={locale.WithoutIntroText}
                          />
                        </RadioGroup>
                      )}
                      name="presentationType"
                      control={control}
                    />
                  </Grid>
                  <Grid item>
                    <Controller
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ""}
                          onChange={(val) => field.onChange(val ?? "")}
                          multiline={true}
                          showWordCount={true}
                          inputProps={{ maxLength: 800, rows: 3 }}
                          fullWidth={true}
                          disabled={
                            watch("presentationType") ===
                            ConsultantPresentationType.NoText
                          }
                        ></TextField>
                      )}
                      name="presentationText"
                      control={control}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item>
            <FormLabel>
              <Typography variant="body1">{locale["Contact person"]}</Typography>
            </FormLabel>
            <div style={{ marginTop: "1rem" }}>
              <Controller
                render={({ field }) => <UserSearchSingleModal {...field} />}
                name="contactPerson"
                control={control}
              />
            </div>
          </Grid>

          {allRefEvolDocsLength > 0 && (
            <Grid item>
              <FormLabel style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body1">
                  {locale.Files}
                  {selectedItems.length > 0 ? ` (${selectedItemsText})` : ""}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    form.setValue(
                      "selectedFiles",
                      documents?.map((x) => x.FileRef.NodeID) ?? []
                    );
                    form.setValue(
                      "selectedReferenceDocuments",
                      (getFilesQuery.data?.consultantReferenceDocuments as any) ?? []
                    );
                  }}
                >
                  {locale.SelectAll}
                </Button>
              </FormLabel>

              {attachmentErrors.length > 0 && (
                <Alert severity="error" style={{ marginTop: "1rem" }}>
                  <AlertTitle>{locale.AttachmentsCorrupted}</AlertTitle>
                  {attachmentErrors.map((x) => (
                    <p>{x.filename}</p>
                  ))}
                </Alert>
              )}

              <div style={{ marginTop: "1rem" }}>
                {getFilesQuery.loading ? (
                  <CircularProgress size={32} />
                ) : (
                  <Box className={classes.fileListContainer}>
                    {documents.length > 0 && (
                      <Box>
                        <Controller
                          render={({ field }) => (
                            <DocumentList
                              {...field}
                              files={documents}
                              label={locale.Files}
                            />
                          )}
                          name="selectedFiles"
                          control={control}
                        />
                      </Box>
                    )}

                    {allRefEvolLength > 0 && (
                      <>
                        <Divider orientation="vertical" flexItem={true} />
                        <Box>
                          <Typography variant="h6" style={{ marginLeft: "2px" }}>
                            {locale.ReferencesAssignmentEvaluations}
                          </Typography>

                          {referenceDocuments["CurrentBoss"]?.length > 0 && (
                            <Controller
                              render={({ field }) => (
                                <ConsultantReferenceDocumentList
                                  {...field}
                                  documents={referenceDocuments["CurrentBoss"]}
                                  groupingKey={
                                    locale.consultantReferenceRelation["CurrentBoss"]
                                  }
                                />
                              )}
                              name="selectedReferenceDocuments"
                              control={control}
                            />
                          )}

                          {referenceDocuments["LatestBoss"]?.length > 0 && (
                            <Controller
                              render={({ field }) => (
                                <ConsultantReferenceDocumentList
                                  {...field}
                                  documents={referenceDocuments["LatestBoss"]}
                                  groupingKey={
                                    locale.consultantReferenceRelation["LatestBoss"]
                                  }
                                />
                              )}
                              name="selectedReferenceDocuments"
                              control={control}
                            />
                          )}

                          {referenceDocuments["FormerBoss"]?.length > 0 && (
                            <Controller
                              render={({ field }) => (
                                <ConsultantReferenceDocumentList
                                  {...field}
                                  documents={referenceDocuments["FormerBoss"]}
                                  groupingKey={
                                    locale.consultantReferenceRelation["FormerBoss"]
                                  }
                                />
                              )}
                              name="selectedReferenceDocuments"
                              control={control}
                            />
                          )}

                          {referenceDocuments["CurrentColleague"]?.length > 0 && (
                            <Controller
                              render={({ field }) => (
                                <ConsultantReferenceDocumentList
                                  {...field}
                                  documents={referenceDocuments["CurrentColleague"]}
                                  groupingKey={
                                    locale.consultantReferenceRelation["CurrentColleague"]
                                  }
                                />
                              )}
                              name="selectedReferenceDocuments"
                              control={control}
                            />
                          )}

                          {referenceDocuments["FormerColleague"]?.length > 0 && (
                            <Controller
                              render={({ field }) => (
                                <ConsultantReferenceDocumentList
                                  {...field}
                                  documents={referenceDocuments["FormerColleague"]}
                                  groupingKey={
                                    locale.consultantReferenceRelation["FormerColleague"]
                                  }
                                />
                              )}
                              name="selectedReferenceDocuments"
                              control={control}
                            />
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </div>
            </Grid>
          )}
        </Grid>
      </FormControl>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  fileListContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1px 1fr",
    gap: "0.5rem",
    width: "100%",
    whiteSpace: "nowrap"
  }
}));
